/**
 * organization-legal — the organization's legal identity fields: the § 14
 * block every ticket and invoice carries and the imprint publishes. Settings
 * and the onboarding stepper each mount it and each save it their own way,
 * so the fields are controlled: the value comes down, every edit goes up as
 * `legal-change` carrying the whole next value.
 *
 * `contact-email-required` is the stepper's: the imprint needs a contact
 * address, so a new organization gives one before it sells, while Settings
 * keeps the field optional for an organization that predates it.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import type { Workspace } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { LocalizeMixin } from "../i18n/localize.ts";

type LegalBlock = NonNullable<Workspace.Workspace["legal"]>;

/** Every legal field as the text a person types; an absent optional one is
 *  the empty string. */
export type OrganizationLegalFields = {
	readonly [K in keyof LegalBlock]-?: string;
};

export function organizationLegalFields(
	legal: Workspace.Workspace["legal"],
): OrganizationLegalFields {
	return {
		legalName: legal?.legalName ?? "",
		address: legal?.address ?? "",
		vatId: legal?.vatId ?? "",
		register: legal?.register ?? "",
		representative: legal?.representative ?? "",
		contactEmail: legal?.contactEmail ?? "",
		phone: legal?.phone ?? "",
	};
}

export function organizationLegalBlock(
	fields: OrganizationLegalFields,
): LegalBlock {
	return {
		legalName: fields.legalName.trim(),
		address: fields.address.trim(),
		vatId: fields.vatId.trim(),
		register: fields.register.trim(),
		representative: fields.representative.trim(),
		contactEmail: fields.contactEmail.trim() || null,
		phone: fields.phone.trim() || null,
	};
}

@schmancyElement("organization-legal")
export class OrganizationLegal extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) value: OrganizationLegalFields =
		organizationLegalFields(null);
	@property({ type: Boolean }) readonly = false;
	@property({ type: Boolean, attribute: "contact-email-required" })
	contactEmailRequired = false;

	private readonly edit =
		(key: keyof OrganizationLegalFields) =>
		(e: CustomEvent<{ value: string }>): void => {
			// Held here before the host answers: autofill edits several fields
			// in one task, and each must build on the one before it.
			this.value = { ...this.value, [key]: e.detail.value };
			this.dispatchEvent(
				new CustomEvent<OrganizationLegalFields>("legal-change", {
					detail: this.value,
				}),
			);
		};

	override render(): TemplateResult {
		return html`
			<div class="flex flex-col gap-4">
				${repeat(
					[
						"legalName",
						"address",
						"vatId",
						"register",
						"representative",
					] as const,
					(key) => key,
					(key) => html`
						<schmancy-input
							label=${this.t(`admin.settings.organization.${key}`)}
							required
							?readonly=${this.readonly}
							.errorMessages=${{
								valueMissing: this.t(
									"admin.settings.organization.requiredFieldMessage",
								),
							}}
							.value=${this.value[key]}
							@input=${this.edit(key)}
						></schmancy-input>
					`,
				)}
				<schmancy-input
					type="email"
					?required=${this.contactEmailRequired}
					label=${this.t("admin.settings.organization.contactEmail")}
					hint=${this.t("admin.settings.organization.contactEmailHint")}
					?readonly=${this.readonly}
					.errorMessages=${{
						valueMissing: this.t(
							"admin.settings.organization.requiredFieldMessage",
						),
						typeMismatch: this.t(
							"admin.settings.organization.emailValidationMessage",
						),
					}}
					.value=${this.value.contactEmail}
					@input=${this.edit("contactEmail")}
				></schmancy-input>
				<schmancy-input
					type="tel"
					label=${this.t("admin.settings.organization.phone")}
					hint=${this.t("admin.settings.organization.phoneHint")}
					?readonly=${this.readonly}
					.value=${this.value.phone}
					@input=${this.edit("phone")}
				></schmancy-input>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"organization-legal": OrganizationLegal;
	}
}
